import { useEffect, useRef, useState } from "react";
import Icon from "./Icon";

const LEAFLET_JS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";

// Load Leaflet from the CDN once (same approach the page uses for XLSX), so no npm install is needed.
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  return new Promise((resolve, reject) => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// Single-place link: opens just this restaurant, never directions.
function placeUrl(r) {
  const q = encodeURIComponent([r.title, r.address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/search/?api=1&query=${q}` +
    (r.placeId ? `&query_place_id=${encodeURIComponent(r.placeId)}` : "");
}

// Directions link from user's current coordinates to this restaurant.
function directionsUrl(r, userLatLng) {
  const dest = encodeURIComponent([r.title, r.address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/dir/?api=1&origin=${userLatLng.lat},${userLatLng.lng}&destination=${dest}`;
}

// Build the popup as DOM nodes (not an HTML string) so restaurant text can't inject markup.
function buildPopup(r, userLatLng) {
  const wrap = document.createElement("div");
  wrap.style.fontFamily = "var(--font-body)";
  wrap.style.minWidth = "160px";

  const name = document.createElement("div");
  name.style.fontWeight = "600";
  name.style.fontSize = "14px";
  name.textContent = (r.favorite ? "😋 " : "") + r.title;
  wrap.appendChild(name);

  const where = r.faveRegion || r.faveCity || r.wcCity || "";
  if (where) {
    const w = document.createElement("div");
    w.style.fontSize = "12px";
    w.style.opacity = "0.7";
    w.textContent = where;
    wrap.appendChild(w);
  }

  const notes = r.faveNotes || r.wcComment;
  if (notes) {
    const n = document.createElement("div");
    n.style.fontSize = "12px";
    n.style.fontStyle = "italic";
    n.style.marginTop = "4px";
    n.textContent = notes;
    wrap.appendChild(n);
  }

  const a = document.createElement("a");
  a.href = userLatLng ? directionsUrl(r, userLatLng) : placeUrl(r);
  a.target = "_blank";
  a.rel = "noreferrer";
  a.textContent = userLatLng ? "Get directions" : "Open in Maps";
  a.style.display = "inline-block";
  a.style.marginTop = "6px";
  a.style.fontSize = "12px";
  wrap.appendChild(a);

  return wrap;
}

// Inject the pulse animation once
function ensurePulseStyle() {
  if (document.getElementById("user-location-pulse-style")) return;
  const style = document.createElement("style");
  style.id = "user-location-pulse-style";
  style.textContent = `
    @keyframes gulliver-pulse {
      0%   { transform: scale(1);   opacity: 0.8; }
      70%  { transform: scale(2.2); opacity: 0; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    .gulliver-user-dot {
      width: 14px; height: 14px;
      background: #D9313A;
      border: 2.5px solid white;
      border-radius: 50%;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      position: relative;
    }
    .gulliver-user-dot::after {
      content: "";
      position: absolute;
      inset: -3px;
      background: #D9313A;
      border-radius: 50%;
      opacity: 0.4;
      animation: gulliver-pulse 2s ease-out infinite;
    }
  `;
  document.head.appendChild(style);
}

export default function RestaurantsMap({ restaurants, filterLabel, onClose }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const [error, setError] = useState(false);
  const [locationStatus, setLocationStatus] = useState(null); // null | "locating" | "found" | "denied"

  const mappable = restaurants.filter(
    r => typeof r.lat === "number" && typeof r.lng === "number" && !r.permanentlyClosed
  );
  const missing = restaurants.filter(r => !r.permanentlyClosed).length - mappable.length;

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then(L => {
      if (cancelled || !mapEl.current) return;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

      const map = L.map(mapEl.current);
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const points = [];
      const markers = [];
      for (const r of mappable) {
        const marker = L.marker([r.lat, r.lng]).addTo(map);
        marker.bindPopup(buildPopup(r, null));
        marker._restaurant = r;
        markers.push(marker);
        points.push([r.lat, r.lng]);
      }

      if (points.length === 1) map.setView(points[0], 15);
      else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
      else map.setView([20, 0], 2);

      // Try to place the user dot
      ensurePulseStyle();
      setLocationStatus("locating");
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => {
            if (cancelled || !mapRef.current) return;
            const { latitude: lat, longitude: lng } = pos.coords;
            const userLatLng = { lat, lng };
            const icon = L.divIcon({
              className: "",
              html: '<div class="gulliver-user-dot"></div>',
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            });
            if (userMarkerRef.current) userMarkerRef.current.remove();
            userMarkerRef.current = L.marker([lat, lng], { icon, zIndexOffset: 1000 })
              .bindPopup("You are here")
              .addTo(mapRef.current);
            // Rebuild popups with directions now that we have coordinates
            markers.forEach(m => m.bindPopup(buildPopup(m._restaurant, userLatLng)));
            setLocationStatus("found");
          },
          () => setLocationStatus("denied"),
          { timeout: 8000, maximumAge: 60000 }
        );
      } else {
        setLocationStatus("denied");
      }
    }).catch(() => setError(true));

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurants]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "var(--paper, #fff)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
        <button className="icon-btn" onClick={onClose} aria-label="Close map">
          <Icon name="back" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>
            {mappable.length} restaurant{mappable.length !== 1 ? "s" : ""} on the map
            {filterLabel ? ` · "${filterLabel}"` : ""}
          </div>
          {missing > 0 && (
            <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>
              {missing} not shown yet (still looking up locations)
            </div>
          )}
        </div>
        {locationStatus === "locating" && (
          <div style={{ fontSize: 11, color: "var(--ink-light)" }}>Locating…</div>
        )}
        {locationStatus === "found" && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--ink-light)" }}>
            <div style={{ width: 8, height: 8, background: "#D9313A", borderRadius: "50%" }} />
            You are here
          </div>
        )}
      </div>
      {error ? (
        <div style={{ padding: 24, color: "var(--ink-light)" }}>Couldn't load the map. Check your connection and try again.</div>
      ) : (
        <div ref={mapEl} style={{ flex: 1, width: "100%" }} />
      )}
    </div>
  );
}
